import * as React from 'react';
import Modal from '@amzn/awsui-components-console/modal';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Button from '@amzn/awsui-components-console/button';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';
import RadioGroup from '@amzn/awsui-components-console/radio-group';
import SyncFrequencyEditor, { SyncFrequency } from './SyncFrequencyEditor';

export interface FormData {
  name: string;
  url: string;
  description: string;
  source: string;
  syncFrequency: SyncFrequency;
}

interface ValidationErrors {
  name?: string;
  url?: string;
  description?: string;
}

interface CollectionModalProps {
  visible: boolean;
  isEditMode: boolean;
  formData: FormData;
  onDismiss: () => void;
  onSubmit: () => void;
  onFormDataChange: (formData: FormData) => void;
}

const CollectionModal: React.FC<CollectionModalProps> = ({ visible, isEditMode, formData, onDismiss, onSubmit, onFormDataChange }) => {
  const [errors, setErrors] = React.useState<ValidationErrors>({});

  // Clear errors when modal opens
  React.useEffect(() => {
    if (visible) {
      setErrors({});
    }
  }, [visible]);

  // Validation functions
  const validateName = (name: string): string | undefined => {
    if (!name.trim()) return 'Name is required';
    if (name.length < 3) return 'Name must be at least 3 characters';
    if (name.length > 100) return 'Name must be less than 100 characters';
    return undefined;
  };

  const validateUrl = (url: string): string | undefined => {
    if (!url.trim()) return 'URL is required';

    // S3 URL validation
    if (url.startsWith('s3://')) {
      const s3Pattern = /^s3:\/\/[a-z0-9.-]+\/.*$/;
      if (!s3Pattern.test(url)) return 'Invalid S3 URL format (e.g., s3://bucket-name/path)';
      return undefined;
    }

    // HTTP/HTTPS URL validation using browser's URL constructor
    try {
      const urlObj = new URL(url);

      // Must be HTTP or HTTPS
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        return 'URL must use HTTP or HTTPS protocol';
      }

      return undefined;
    } catch {
      return 'Please enter a valid URL';
    }
  };

  // Inline validation handlers
  const handleNameChange = (name: string) => {
    onFormDataChange({ ...formData, name });
    setErrors((prev) => ({ ...prev, name: validateName(name) }));
  };

  const handleUrlChange = (url: string) => {
    onFormDataChange({ ...formData, url });
    // Only validate URL in create mode
    if (!isEditMode) {
      setErrors((prev) => ({ ...prev, url: validateUrl(url) }));
    }
  };

  const handleDescriptionChange = (description: string) => {
    onFormDataChange({ ...formData, description });
    setErrors((prev) => ({
      ...prev,
      description: !description.trim() ? 'Description is required' : undefined,
    }));
  };

  // Check if form is valid
  const isFormValid = () => {
    const hasErrors = Object.values(errors).some((error) => error);
    const hasEmptyRequired = !formData.name.trim() || !formData.url.trim() || !formData.description.trim();
    return !hasErrors && !hasEmptyRequired;
  };

  const handleSubmit = () => {
    // Validate all fields before submission
    const nameError = validateName(formData.name);
    const urlError = isEditMode ? undefined : validateUrl(formData.url);
    const descriptionError = !formData.description.trim() ? 'Description is required' : undefined;

    const newErrors = { name: nameError, url: urlError, description: descriptionError };
    setErrors(newErrors);

    // Only submit if no errors
    if (!Object.values(newErrors).some((error) => error)) {
      onSubmit();
    }
  };
  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={isEditMode ? 'Edit Content Collection' : 'Create Content Collection'}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <div className="bright-yellow-button">
              <Button variant="primary" onClick={handleSubmit} disabled={!isFormValid()}>
                {isEditMode ? 'Save' : 'Create'}
              </Button>
            </div>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField label="Name" description="Enter the name of the content collection" errorText={errors.name}>
          <Input
            value={formData.name}
            onChange={({ detail }) => handleNameChange(detail.value)}
            placeholder="Enter collection name"
            invalid={!!errors.name}
          />
        </FormField>

        <FormField label="URL" description="Enter the URL of the data source" errorText={!isEditMode ? errors.url : undefined}>
          <Input
            value={formData.url}
            onChange={({ detail }) => handleUrlChange(detail.value)}
            placeholder="https://example.com or s3://bucket/path"
            disabled={isEditMode}
            invalid={!isEditMode && !!errors.url}
          />
        </FormField>

        <FormField label="Description" description="Enter a description for the collection" errorText={errors.description}>
          <Input
            value={formData.description}
            onChange={({ detail }) => handleDescriptionChange(detail.value)}
            placeholder="Enter description"
            invalid={!!errors.description}
          />
        </FormField>

        <FormField label="Data Source" description="Select the type of data source">
          <RadioGroup
            value={formData.source}
            direction="horizontal"
            onChange={({ detail }) => onFormDataChange({ ...formData, source: detail.value })}
            items={[
              { value: 'Quip', label: 'Quip', disabled: isEditMode },
              { value: 'Web', label: 'Web', disabled: isEditMode },
              { value: 'Highspot', label: 'Highspot', disabled: isEditMode },
              { value: 'Wiki', label: 'Wiki', disabled: isEditMode },
              { value: 'BYOC', label: 'BYOC', disabled: isEditMode },
            ]}
          />
        </FormField>

        <SyncFrequencyEditor
          value={formData.syncFrequency}
          onChange={(syncFrequency) => onFormDataChange({ ...formData, syncFrequency })}
        />
      </SpaceBetween>
    </Modal>
  );
};

export default CollectionModal;
